import { all, call, fork, put, takeEvery, select } from "redux-saga/effects"

// Toast for notifications
import { toast } from "react-toastify"
import {
  GET_DISPUTES,
  GET_DISPUTE,
  CREATE_DISPUTE,
  UPDATE_DISPUTE,
  DELETE_DISPUTE,
  CHANGE_DISPUTE_STATUS,
  ADD_DISPUTE_LOG,
  RESPOND_TO_DISPUTE,
  REJECT_DISPUTE,
} from "./actionTypes"
import {
  getDisputesSuccess,
  getDisputesFail,
  getDisputeSuccess,
  getDisputeFail,
  createDisputeSuccess,
  createDisputeFail,
  updateDisputeSuccess,
  updateDisputeFail,
  deleteDisputeSuccess,
  deleteDisputeFail,
  changeDisputeStatusSuccess,
  changeDisputeStatusFail,
  addDisputeLogSuccess,
  addDisputeLogFail,
  respondToDisputeSuccess,
  respondToDisputeFail,
  rejectDisputeSuccess,
  rejectDisputeFail,
  getDispute,
} from "./actions"
import {
  getDisputes,
  getDisputeById,
  createDispute,
  updateDispute as updateDisputeApi,
  deleteDispute as deleteDisputeApi,
  changeDisputeStatus as changeDisputeStatusApi,
  addDisputeLog as addDisputeLogApi,
  respondToDispute as respondToDisputeApi,
  rejectDispute as rejectDisputeApi,
} from "../../helpers/backend_helper"

// Get All Disputes
function* fetchDisputes({ payload: { params, history } }) {
  try {
    const response = yield getDisputes(params)
    yield put(getDisputesSuccess(response))
  } catch (error) {
    yield put(getDisputesFail(error))
  }
}

// Get Dispute By ID
function* fetchDispute({ payload: { disputeId, history } }) {
  try {
    const response = yield getDisputeById(disputeId)
    yield put(getDisputeSuccess(response))
  } catch (error) {
    yield put(getDisputeFail(error))
  }
}

// Create Dispute
function* onCreateDispute({ payload: { data, history } }) {
  try {
    const response = yield createDispute(data)
    yield put(createDisputeSuccess(response))
    if (history) {
      history.push("/disputes")
    }
  } catch (error) {
    yield put(createDisputeFail(error))
  }
}

// Update Dispute
function* onUpdateDispute({ payload: { disputeId, data, history } }) {
  try {
    const response = yield updateDisputeApi(disputeId, data)
    yield put(updateDisputeSuccess(response))
  } catch (error) {
    yield put(updateDisputeFail(error))
  }
}

// Delete Dispute
function* onDeleteDispute({ payload: { disputeId, history } }) {
  try {
    const response = yield deleteDisputeApi(disputeId)
    yield put(deleteDisputeSuccess(disputeId))
  } catch (error) {
    yield put(deleteDisputeFail(error))
  }
}

// Change Dispute Status
function* onChangeDisputeStatus({ payload: { disputeId, statusData, history } }) {
  try {
    const response = yield changeDisputeStatusApi(disputeId, statusData)

    // Check if response indicates an actual error (be more conservative)
    // Only treat as error if explicitly marked as such
    const hasError = response?.hasError === true ||
                      response?.isError === true ||
                      response?.status === "Error" ||
                      response?.statusCode === 400 ||
                      response?.statusCode === 500

    if (hasError) {
      const errorMsg = response?.message || response?.errorMessage || "Failed to update dispute status"
      toast.error(errorMsg)
      yield put(changeDisputeStatusFail({ ...response, message: errorMsg }))
      return
    }

    // Dispatch success with the updated dispute data
    // Put response first, then override with our known values
    yield put(changeDisputeStatusSuccess({
      ...response,
      id: disputeId,
      statusId: statusData.statusId,
    }))
    toast.success("Dispute status updated successfully!")

    // Refresh the specific dispute to get full updated data from backend
    const updatedDispute = yield call(getDisputeById, disputeId)
    yield put(getDisputeSuccess(updatedDispute))

    // Small delay to ensure the dispute is updated first
    yield new Promise(resolve => setTimeout(resolve, 100))

    // Refresh the disputes list with current pagination
    const state = yield select()
    const currentPage = state.Disputes?.pagination?.currentPage || 0

    const disputesResponse = yield call(getDisputes, {
      PageSize: 10,
      PageIndex: currentPage,
    })
    yield put(getDisputesSuccess(disputesResponse))
  } catch (error) {
    yield put(changeDisputeStatusFail(error))
    // Extract error message from response if available
    let errorMsg = "Failed to update dispute status"

    if (error?.response) {
      // Handle axios error with response
      const data = error.response.data
      errorMsg = data?.message || data?.title || data?.error || `Server Error (${error.response.status})`
    } else if (error?.message) {
      // Handle network error or other errors
      errorMsg = error.message
    } else if (typeof error === "string") {
      errorMsg = error
    }

    toast.error(errorMsg)
  }
}

// Add Dispute Log
function* onAddDisputeLog({ payload: { disputeId, logData, history } }) {
  try {
    const response = yield addDisputeLogApi(disputeId, logData)
    yield put(addDisputeLogSuccess(response))
  } catch (error) {
    yield put(addDisputeLogFail(error))
  }
}

// Respond to Dispute
function* onRespondToDispute({ payload: { disputeId, responseData, history } }) {
  try {
    const response = yield respondToDisputeApi(disputeId, responseData)

    // Check if response indicates an actual error (be more conservative)
    // Only treat as error if explicitly marked as such
    const hasError = response?.hasError === true ||
                      response?.isError === true ||
                      response?.status === "Error" ||
                      response?.statusCode === 400 ||
                      response?.statusCode === 500

    if (hasError) {
      const errorMsg = response?.message || response?.errorMessage || "Failed to submit response"
      toast.error(errorMsg)
      yield put(respondToDisputeFail({ ...response, message: errorMsg }))
      return
    }

    yield put(respondToDisputeSuccess(response))
    toast.success("Response submitted successfully!")

    // Refresh the specific dispute to get full updated data from backend
    const updatedDispute = yield call(getDisputeById, disputeId)
    yield put(getDisputeSuccess(updatedDispute))

    // Small delay to ensure the dispute is updated first
    yield new Promise(resolve => setTimeout(resolve, 100))

    // Refresh the disputes list with current pagination
    const state = yield select()
    const currentPage = state.Disputes?.pagination?.currentPage || 0

    const disputesResponse = yield call(getDisputes, {
      PageSize: 10,
      PageIndex: currentPage,
    })
    yield put(getDisputesSuccess(disputesResponse))
  } catch (error) {
    yield put(respondToDisputeFail(error))
    // Extract error message from response if available
    let errorMsg = "Failed to submit response"

    if (error?.response) {
      // Handle axios error with response
      const data = error.response.data
      errorMsg = data?.message || data?.title || data?.error || `Server Error (${error.response.status})`
    } else if (error?.message) {
      // Handle network error or other errors
      errorMsg = error.message
    } else if (typeof error === "string") {
      errorMsg = error
    }

    toast.error(errorMsg)
  }
}

// Reject Dispute
function* onRejectDispute({ payload: { disputeId, rejectionData, history } }) {
  try {
    const response = yield rejectDisputeApi(disputeId, rejectionData)

    // Check if response indicates an actual error (be more conservative)
    // Only treat as error if explicitly marked as such
    const hasError = response?.hasError === true ||
                      response?.isError === true ||
                      response?.status === "Error" ||
                      response?.statusCode === 400 ||
                      response?.statusCode === 500

    if (hasError) {
      const errorMsg = response?.message || response?.errorMessage || "Failed to reject dispute"
      toast.error(errorMsg)
      yield put(rejectDisputeFail({ ...response, message: errorMsg }))
      return
    }

    yield put(rejectDisputeSuccess(response))
    toast.success("Dispute rejected successfully!")

    // Refresh the specific dispute to get full updated data from backend
    const updatedDispute = yield call(getDisputeById, disputeId)
    yield put(getDisputeSuccess(updatedDispute))

    // Small delay to ensure the dispute is updated first
    yield new Promise(resolve => setTimeout(resolve, 100))

    // Refresh the disputes list with current pagination
    const state = yield select()
    const currentPage = state.Disputes?.pagination?.currentPage || 0

    const disputesResponse = yield call(getDisputes, {
      PageSize: 10,
      PageIndex: currentPage,
    })
    yield put(getDisputesSuccess(disputesResponse))
  } catch (error) {
    yield put(rejectDisputeFail(error))
    // Extract error message from response if available
    let errorMsg = "Failed to reject dispute"

    if (error?.response) {
      // Handle axios error with response
      const data = error.response.data
      errorMsg = data?.message || data?.title || data?.error || `Server Error (${error.response.status})`
    } else if (error?.message) {
      // Handle network error or other errors
      errorMsg = error.message
    } else if (typeof error === "string") {
      errorMsg = error
    }

    toast.error(errorMsg)
  }
}

export function* disputesSaga() {
  yield takeEvery(GET_DISPUTES, fetchDisputes)
  yield takeEvery(GET_DISPUTE, fetchDispute)
  yield takeEvery(CREATE_DISPUTE, onCreateDispute)
  yield takeEvery(UPDATE_DISPUTE, onUpdateDispute)
  yield takeEvery(DELETE_DISPUTE, onDeleteDispute)
  yield takeEvery(CHANGE_DISPUTE_STATUS, onChangeDisputeStatus)
  yield takeEvery(ADD_DISPUTE_LOG, onAddDisputeLog)
  yield takeEvery(RESPOND_TO_DISPUTE, onRespondToDispute)
  yield takeEvery(REJECT_DISPUTE, onRejectDispute)
}

export default disputesSaga
